import {useAuth} from '@/components/AuthProvider';
import {Button} from '@/components/ui/button';
import {ShareSheet} from '@/components/shareSheet/shareSheet';
import {useAnalytics} from '@/hooks/useAnalytics';
import {useIsMobile} from '@/hooks/useIsMobile';
import {Check, Share} from 'lucide-react';
import {useCallback, useState} from 'react';

interface ShareButtonProps {
  title: string;
  contentType?: 'list' | 'event';
  contentId?: string;
  variant?: 'outline' | 'overlay';
  size?: 'sm' | 'lg';
  iconOnly?: boolean;
}

/**
 * Builds a shareable URL by appending UTM attribution params
 * to the current page URL.
 */
function buildShareUrl(userId: string | undefined): string {
  const url = new URL(window.location.href);
  url.searchParams.set('utm_source', 'share');
  if (userId) {
    url.searchParams.set('shared_by', userId);
  }
  return url.toString();
}

/**
 * Adaptive share button that opens the native share sheet on mobile
 * devices, or copies the current URL to the clipboard on desktop
 * with animated success feedback. Appends UTM attribution params
 * and fires a Mixpanel tracking event.
 */
export function ShareButton({
  title,
  contentType,
  contentId,
  variant = 'outline',
  size = 'sm',
  iconOnly = false,
}: ShareButtonProps) {
  const [copied, setCopied] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const isMobile = useIsMobile();
  const {track} = useAnalytics();
  const {profile} = useAuth();

  const handleShare = useCallback(async () => {
    const url = buildShareUrl(profile?.id);

    if (isMobile && contentType === 'event' && contentId) {
      track('Share Sheet Opened', {
        platform: 'mobile',
        content_type: contentType,
        content_id: contentId,
        shared_by: profile?.id,
      });
      setSheetOpen(true);
      return;
    }

    track('Share Clicked', {
      platform: isMobile ? 'mobile' : 'desktop',
      content_type: contentType,
      content_id: contentId,
      shared_by: profile?.id,
    });

    if (isMobile && navigator.share) {
      try {
        await navigator.share({title, url});
      } catch {
        // User cancelled the share sheet — no action needed
      }
      return;
    }

    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard access denied — fail silently
    }
  }, [title, isMobile, profile?.id, contentType, contentId, track]);

  return (
    <>
      <Button
        variant={variant}
        size={iconOnly ? 'icon' : size}
        onClick={handleShare}
        aria-label={iconOnly ? (copied ? 'Copied' : 'Share') : undefined}
        className={`transition-colors duration-200 ${iconOnly && size === 'lg' ? 'size-10' : ''} ${copied ? 'border-green-600 text-green-600' : ''}`}
      >
        <span className="relative size-4">
          <Share
            aria-hidden="true"
            className={`absolute inset-0 transition-opacity duration-200 ${copied ? 'opacity-0' : 'opacity-100'}`}
          />
          <Check
            aria-hidden="true"
            className={`absolute inset-0 transition-opacity duration-200 ${copied ? 'opacity-100' : 'opacity-0'}`}
          />
        </span>
        {!iconOnly && (copied ? 'Copied' : 'Share')}
      </Button>
      {contentType === 'event' && contentId && (
        <ShareSheet
          isOpen={sheetOpen}
          onClose={() => setSheetOpen(false)}
          eventId={contentId}
          eventTitle={title}
        />
      )}
    </>
  );
}
